import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Link, useMatch } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../../../../../../AppContext";
import "../style.scss";

function ListClient(props) {
  const [table, setTable] = useState({
    head: ["#", "Username", "Group", "Email", "Actions"],
    data: null,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [groups, setGroups] = useState([]);
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export

  // Ref to track previous export state
  const prevExport = useRef(props?.export || false);

  const { user, chimeraserv, components } = useAppContext();
  const contextProps = { user, chimeraserv, components };

  // Get the base path for links
  const match = useMatch("/app");
  const basePath = "/app";

  useEffect(() => {
    const fetchData = async () => {
      if (contextProps.user && contextProps.user.data) {
        setLoad(0); // Start loading
        try {
          // Fetch users
          const userResponse = await axios.get(
            contextProps.chimeraserv.urlGet + "/user",
            {
              headers: {
                Authorization: contextProps.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );
          let data = [];
          if (userResponse.data && Object.keys(userResponse.data).length) {
            data = userResponse.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));

          // Fetch groups
          const groupResponse = await axios.get(
            contextProps.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: contextProps.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );

          setGroups(groupResponse.data || []);
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setGroups([]);
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [contextProps.user, contextProps.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== null) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Filter and search logic using useMemo for performance
  const filteredData = useMemo(() => {
    if (!table.data) return [];

    return table.data
      .filter((data) => !(data.usrKey === contextProps.user.data.usrKey))
      .filter((user) => {
        // Find the group for this user
        const userGroup = groups.find((g) => g._id === user.group);
        const groupName = userGroup ? userGroup.name : "N/A";

        // Search filter - includes username, email, and group name
        const matchesSearch =
          searchTerm === "" ||
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          groupName.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter
        const matchesStatus =
          filterStatus === "all" ||
          (filterStatus === "active" && user.status === 1) ||
          (filterStatus === "inactive" && user.status === 0);

        return matchesSearch && matchesStatus;
      });
  }, [
    table.data,
    searchTerm,
    filterStatus,
    contextProps.user.data.usrKey,
    groups,
  ]);

  // Export logic
  useEffect(() => {
    if (prevExport.current !== props?.export && load === 2 && props?.getData) {
      prevExport.current = props.export;

      // Prepare export data based on filtered results
      const exportData = filteredData.map((user, index) => {
        const userGroup = groups.find((g) => g._id === user.group);
        const groupName = userGroup ? userGroup.name : "N/A";
        const status = user.status === 1 ? "Active" : "Inactive";

        return [
          (index + 1).toString(),
          user.username,
          groupName,
          user.email,
          status,
        ];
      });

      // Get total count for summary
      const totalUsers = filteredData.length;
      const activeUsers = filteredData.filter((u) => u.status === 1).length;
      const inactiveUsers = filteredData.filter((u) => u.status === 0).length;

      // Create summary data
      const summaryData = [
        ["Total Users", totalUsers.toString()],
        ["Active Users", activeUsers.toString()],
        ["Inactive Users", inactiveUsers.toString()],
        ...(searchTerm || filterStatus !== "all"
          ? [
              ["Filtered Results", "Yes"],
              ...(searchTerm ? [["Search Term", searchTerm]] : []),
              ...(filterStatus !== "all"
                ? [
                    [
                      "Status Filter",
                      filterStatus.charAt(0).toUpperCase() +
                        filterStatus.slice(1),
                    ],
                  ]
                : []),
            ]
          : []),
      ];

      props.getData({
        type: "table",
        title: "User Management Summary",
        body: [
          // Main user data table
          {
            head: [["#", "Username", "Group", "Email", "Status"]],
            body: exportData,
            sheetName: "User_List",
          },
          // Summary table
          {
            head: [["Summary", "Count"]],
            body: summaryData,
            sheetName: "Summary",
          },
        ],
        id: "exportPdf_UserList",
      });
    }
    // eslint-disable-next-line
  }, [
    props?.export,
    load,
    filteredData,
    groups,
    searchTerm,
    filterStatus,
    props?.getData,
  ]);

  const valueChange = async (data, key) => {
    const updatedStatus = data.status === 1 ? 0 : 1;
    const updatedData = { ...data, status: updatedStatus };

    // Optimistically update UI
    setTable((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item._id === data._id ? { ...item, status: updatedStatus } : item
      ),
    }));

    try {
      await axios.put(
        contextProps.chimeraserv.urlPost + "/user/update",
        updatedData,
        {
          headers: {
            Authorization: contextProps.user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      // Optionally refetch or handle success
    } catch (error) {
      // Revert UI change on error
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === data._id ? { ...item, status: data.status } : item
        ),
      }));
      console.error("Error updating user status:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
  };

  const renderData = () =>
    filteredData.map((data, key) => {
      const group = groups.find((g) => g._id === data.group);
      return (
        <tr key={data._id}>
          <td>{key + 1}</td>
          <td>
            <div className="user-info">
              <strong>{data.username}</strong>
            </div>
          </td>
          <td>{group ? group.name : "N/A"}</td>
          <td>{data.email}</td>
          <td className="actions">
            <label className="switch">
              <input
                type="checkbox"
                id={data._id}
                checked={!!data.status}
                onChange={() => valueChange(data, key)}
              />
              <span className="slider"></span>
            </label>
            <Link to={`${basePath}/user/edit/${data._id}`}>
              <FontAwesomeIcon className="icon" icon={["fas", "cog"]} /> Edit
            </Link>
            <Link to={`${basePath}/user/view/${data._id}`}>
              <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
            </Link>
          </td>
        </tr>
      );
    });

  const Loader = contextProps.components.setting[1].component;

  return (
    <div className="custForm" id="exportPdf_UserList">
      {!table.data ? (
        <Loader />
      ) : (
        <div className="table-container">
          <table className="table table-striped defaultTable">
            <thead>
              <tr>
                {table.head.map((head, key) => (
                  <th scope="col" key={key}>
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                renderData()
              ) : (
                <tr>
                  <td
                    colSpan={table.head.length}
                    className="text-center no-results"
                  >
                    {searchTerm || filterStatus !== "all" ? (
                      <div>
                        <FontAwesomeIcon
                          icon={["fas", "search"]}
                          className="no-results-icon"
                        />
                        <p>No users found matching your search criteria</p>
                        <button className="btn btn-link" onClick={clearFilters}>
                          Clear filters to see all users
                        </button>
                      </div>
                    ) : (
                      <div>
                        <FontAwesomeIcon
                          icon={["fas", "users"]}
                          className="no-results-icon"
                        />
                        <p>No users available</p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ListClient;
